<template>
  <div>
    <h3 class="text-subtitle-1 font-weight-bold mb-4 d-flex align-center ga-2">
      <v-icon
        size="18"
        color="primary"
      >
        mdi-image-multiple-outline
      </v-icon>
      {{ $t('onboarding.wizard.data-entry.company-images') }}
    </h3>

    <p class="text-body-2 text-medium-emphasis mb-4">
      {{ $t('onboarding.wizard.data-entry.company-images-hint') }}
    </p>

    <v-row class="mb-6 onboarding-company-images-row">
      <CompanyImageSlot
        v-if="isLogoEditable"
        kind="logo"
        field-key="companyLogo"
        :label="$t('settings.company.images.labels.company-logo')"
        :md="4"
        crop-name="logo"
        :min-height="100"
        :min-width="1200"
        :ratio="0"
        :display-url="logoDisplayUrl"
        :ai-captured="aiCapturedFields.has('companyLogo')"
        :uploading="isUploading('logo')"
        :can-upload="canUpload"
        :persist-image="persistImage"
      />
      <CompanyImageSlot
        kind="team"
        field-key="teamPicture"
        :label="$t('settings.company.images.labels.team-picture')"
        :md="isLogoEditable ? 4 : 6"
        crop-name="team"
        :min-height="800"
        :min-width="1200"
        :ratio="1.5"
        :display-url="teamDisplayUrl"
        :ai-captured="aiCapturedFields.has('teamPicture')"
        :uploading="isUploading('team')"
        :can-upload="canUpload"
        :persist-image="persistImage"
      />
      <CompanyImageSlot
        kind="profile"
        field-key="portraitImage"
        :label="$t('settings.company.images.labels.portrait-picture')"
        :md="isLogoEditable ? 4 : 6"
        crop-name="image"
        :min-height="1200"
        :min-width="1200"
        :ratio="1"
        :display-url="profileDisplayUrl"
        :ai-captured="aiCapturedFields.has('portraitImage')"
        :uploading="isUploading('profile')"
        :can-upload="canUpload"
        :persist-image="persistImage"
      />
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useOnboardingDataEntryContext } from '../../composables/onboardingDataEntryContext'
import { useOnboardingFeatures } from '../../composables/useOnboardingFeatures'
import { useOnboardingFormImages } from '../../composables/useOnboardingFormImages'
import type { OnboardingWizardCompany } from '../../lib/onboardingWizardCompany'
import CompanyImageSlot from './CompanyImageSlot.vue'

const props = defineProps<{
  wizardCompany?: OnboardingWizardCompany | null
}>()

const emit = defineEmits<{
  'image-uploaded': [payload: { kind: 'logo' | 'team' | 'profile'; url?: string }]
}>()

const { isLogoEditable } = useOnboardingFeatures()
const { formData, aiCapturedFields } = useOnboardingDataEntryContext()

const canUpload = computed(() => Boolean(props.wizardCompany?.id))

const { logoDisplayUrl, teamDisplayUrl, profileDisplayUrl, isUploading, persistImage } =
  useOnboardingFormImages({
    wizardCompany: () => props.wizardCompany,
    onUploaded: (payload) => emit('image-uploaded', payload),
    formValues: {
      logo: () => formData.value.companyLogo,
      team: () => formData.value.teamPicture,
      profile: () => formData.value.portraitImage,
    },
  })
</script>

<style scoped>
.onboarding-company-images-row {
  align-items: flex-start;
}
</style>
